import React, { useContext } from 'react';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalContent,
  StudioContext,
  ModalSize,
  UserContext,
  MODAL_FOOTER_PRIMARY_BTN_ID,
} from 'venn-components';
import { CheckboxWrapper, GetColor } from 'venn-ui-kit';
import styled, { ThemeContext, css, type CSSObject } from 'styled-components';
import { toggleStudioTemplateType, useHasFF } from 'venn-utils';
import ReportingSection from './ReportSettings';
import Details from './Details';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { analysisViewTypeState, isReportState, isTemplateState } from 'venn-state';
import { getTopBarColor, type GetTopBarColorType } from '../top-bar/shared';

export interface SetupConfigurationModalProps {
  onCancel: () => void;
}

const TemplateConfiguration = () => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const { hasPermission } = useContext(UserContext);
  const isReport = useRecoilValue(isReportState);
  const isTemplate = useRecoilValue(isTemplateState);
  const barColors = getTopBarColor({ isReport, isTemplate });

  const theme = useContext(ThemeContext);

  const onChange = useRecoilCallback(
    ({ set }) =>
      () => {
        set(analysisViewTypeState, (viewType) => toggleStudioTemplateType(viewType));
      },
    [],
  );

  return (
    <TemplateWrapper barColors={barColors}>
      <CheckboxWrapper
        checked={isTemplate}
        onChange={onChange}
        disabled={!hasPermission('STUDIO_CREATE_TEMPLATE')}
        justify="left"
        inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
      >
        Assign as a template
      </CheckboxWrapper>
    </TemplateWrapper>
  );
};

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const SetupConfigurationModal = React.memo(({ onCancel }: SetupConfigurationModalProps) => {
  const { isCheckingDuplicateReportName } = useContext(StudioContext);
  const isReport = useRecoilValue(isReportState);
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const theme = useContext(ThemeContext);

  const customButtonStyle: React.CSSProperties = hasCustomizationEnhancements
    ? {
        background: theme.Colors.DarkBlue,
        color: theme.Colors.White,
        border: 0,
        borderRadius: '4px',
        width: '80px',
        padding: '10px 12px',
      }
    : {};

  const customFooterStyle: CSSObject = hasCustomizationEnhancements
    ? {
        background: theme.Colors.White,
        borderTop: `1px solid ${theme.Colors.GreyScale.Grey30}`,
      }
    : {};

  return (
    <StyledModal
      closeOnEsc
      initialFocus={`#${MODAL_FOOTER_PRIMARY_BTN_ID}`}
      onClose={onCancel}
      className="qa-configuration-modal"
      size={ModalSize.Medium}
      testId="setup-configuration-modal"
    >
      <StyledModalHeader>{isReport ? 'Report Lab' : 'Studio Analysis'} Setup</StyledModalHeader>
      <StyledModalContent>
        <div className={hasCustomizationEnhancements ? 'my-[16px] mx-[24px]' : 'my-[10px] mx-[20px]'}>
          <TemplateConfiguration />
          <Details />
          <ReportingSection />
        </div>
      </StyledModalContent>
      <StyledModalFooter
        primaryLabel="Close"
        onPrimaryClick={onCancel}
        primaryDisabled={isCheckingDuplicateReportName}
        primaryCustomStyle={customButtonStyle}
        customFooterStyle={customFooterStyle}
      />
    </StyledModal>
  );
});

export default SetupConfigurationModal;

const StyledModal = styled(Modal)`
  &.modal-wrapper {
    min-height: 600px;
    height: 800px;
  }
`;

const StyledModalHeader = styled(ModalHeader)`
  margin: 0;
  border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
  padding: 0 20px 10px 20px;
`;
const StyledModalFooter = styled(ModalFooter)<{ customFooterStyle?: CSSObject }>`
  margin-top: 0;
  border: 0;
  ${({ customFooterStyle }) => customFooterStyle && css(customFooterStyle)}
`;

const StyledModalContent = styled(ModalContent)`
  display: block;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-y: auto;
`;

const TemplateWrapper = styled.div<{ barColors: GetTopBarColorType }>`
  color: ${GetColor.White};
  padding: 3px 10px;
  background: ${({ barColors }) => barColors.Background};
`;
